import { WiredBackButton } from '@euler/app/components/back-button';
import { useOcrImageCapture } from '@euler/app/flows/common/functions/useOcrImageCapture.web';
import { MobileWebCameraView } from '@euler/app/flows/media/components/MobileWebCameraView';
import { withMediaFilePicker } from '@euler/app/flows/media/functions/useMediaFilePicker.web';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { isMobileBrowser } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ResourceAccessScope } from '@euler/model/enum';
import { VehicleServiceInfo } from '@euler/services';
import { useServiceFactory } from '@euler/services/factory';
import { showAlert } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect } from 'react';
import { TouchableNativeFeedback, View } from 'react-native';
import { Modes, PlateVinScanProps } from './PlateVinScanScreen.shared';

export const PlateVinScanScreen = wrapNavigatorScreen(
  (props: PlateVinScanProps) => {
    const { rpx } = useResponsiveContext();
    const { ocrService, vehicleInfoService } = useServiceFactory();

    const navigation = useNavigation<StackNavigationProp<any>>();
    const loading = useAppLoading();

    const { mode = Modes[0], onConfirm } = props;

    const recognizeLicensePlateNo = useCallback(
      async (dataUri: string) => {
        const ocrResult = await ocrService.licensePlateNoRecognize({
          uri: dataUri,
        });
        if (ocrResult.result == null) {
          void showAlert('车牌无法识别!');
          return;
        }
        const licensePlateNo = ocrResult.result.number;
        let serviceInfo: VehicleServiceInfo | undefined = undefined;
        try {
          serviceInfo =
            (await vehicleInfoService.listVehicleServiceInfo({
              scope: ResourceAccessScope.Org,
              plateno: licensePlateNo,
            })) ?? undefined;
        } catch (e) {
          console.error(e);
        }
        onConfirm?.(mode, licensePlateNo, ocrResult.url, serviceInfo);
      },
      [mode, ocrService, onConfirm, vehicleInfoService],
    );

    const recognizeVIN = useCallback(
      async (dataUri: string) => {
        const ocrResult = await ocrService.vinRecognize({
          uri: dataUri,
        });
        if (ocrResult.result == null) {
          void showAlert('VIN无法识别!');
          return;
        }
        const vin = ocrResult.result;
        let serviceInfo: VehicleServiceInfo | undefined = undefined;
        try {
          serviceInfo =
            (await vehicleInfoService.listVehicleServiceInfo({
              scope: ResourceAccessScope.Org,
              vin,
            })) ?? undefined;
        } catch (e) {
          console.error(e);
        }
        onConfirm?.(mode, vin, ocrResult.url, serviceInfo);
      },
      [mode, ocrService, onConfirm, vehicleInfoService],
    );

    const onFileSelected = usePersistFn(async (dataUri: string) => {
      await loading.showWithAction(async () => {
        if (mode === 'plate') {
          await recognizeLicensePlateNo(dataUri);
        } else {
          await recognizeVIN(dataUri);
        }
      });
    });

    useEffect(() => {
      if (isMobileBrowser()) return;

      withMediaFilePicker('photo').then(
        async files => {
          navigation.pop();
          const file = files[0];
          if (!file) return;
          const result = URL.createObjectURL(file);
          await onFileSelected(result);
        },
        () => {
          navigation.pop();
        },
      );
    }, [navigation, onFileSelected]);

    //#region mobile browser specific handling
    const { maskSize } = useOcrImageCapture({
      mode,
    });

    //#endregion

    const onPhotoTaken = usePersistFn(async (dataUri: string) => {
      navigation.pop();
      void onFileSelected(dataUri);
    });

    if (isMobileBrowser()) {
      return (
        <View
          css={`
            flex: 1;
          `}
        >
          <MobileWebCameraView mask={maskSize} onPhotoTaken={onPhotoTaken} />
          <WiredBackButton
            iconColor="white"
            css={`
              position: absolute;
              left: ${rpx(20)}px;
              top: ${rpx(20)}px;
            `}
          />
        </View>
      );
    }

    return (
      <TouchableNativeFeedback onPress={() => navigation.pop()}>
        <View
          css={`
            flex: 1;
          `}
        />
      </TouchableNativeFeedback>
    );
  },
  {
    animationEnabled: false,
    cardStyle: {
      backgroundColor: 'transparent',
    },
    headerShown: false,
    detachPreviousScreen: false,
  },
);
